import { OptionModel, Options } from '$tablet-app/src/app/options/types';
import { notNullOrUndefined } from '$tablet-app/src/app/utils/arrays';
import {
  ListRecipesQueryVariables,
  MealType,
  ModelRecipeFilterInput,
} from '@akin-home/amplify';
import { Recipe } from '../types';
import { SpecialRecipeFilterOption } from './types';

/** Given a fitler group and list of options, get the options for the filter group */
export const getOptionsFromFilterGroup = (
  group: SpecialRecipeFilterOption,
  options: Options
): OptionModel[] => {
  return group.refs
    .map(({ type, code }) => options[type]?.find((opt) => opt.code === code))
    .filter(notNullOrUndefined) as OptionModel[];
};

/** Filters given recipe by list of options */
export const filterRecipeByOptions = (
  recipe: Recipe,
  options: OptionModel[],
  mode: 'or' | 'and' = 'or'
): boolean => {
  return mode === 'or'
    ? options.some(checkRecipeHasOption(recipe))
    : options.every(checkRecipeHasOption(recipe));
};

/** Checks if recipe has matching name */
export const checkRecipeMatchName = (recipe: Recipe, name: string): boolean =>
  recipe.name.toLowerCase().includes(name);

/**
 * Returns true if the given recipe has a matching option
 */
export const checkRecipeHasOption =
  (recipe: Recipe) =>
  (option: OptionModel): boolean => {
    const optionID = option.id;

    switch (option.type) {
      case 'skill_level':
        return recipe.skillLevel?.id === optionID;

      case 'preparation_time':
        return recipe.prepTime?.id === optionID;

      case 'main_ingredient':
        return recipe.mainIngredient?.id === optionID;

      case 'dish_type':
        return recipe.dishType?.id === optionID;

      case 'serving_size':
        return recipe.servingSize?.id === optionID;

      case 'cuisine':
        return recipe.cuisine?.id === optionID;

      case 'special_diet':
        return !!recipe.specialDiets?.some((diet) => diet.id === optionID);

      default:
        return false;
    }
  };

interface SearchScreenFilterValues {
  name?: string;
  dropDownOptions?: OptionModel[];
  filterGroupOptions?: OptionModel[];
}

/** Filters recipes by filters present on the search screen */
export const filterRecipesForSearchScreen = (
  recipes: Recipe[],
  filters: SearchScreenFilterValues
): Recipe[] => {
  return recipes.filter((recipe) => {
    const combinedFilters = [];

    // Filter by name
    if (filters.name) {
      combinedFilters.push(checkRecipeMatchName(recipe, filters.name));
    }

    // Filter by group options
    if (filters.filterGroupOptions) {
      combinedFilters.push(
        filterRecipeByOptions(recipe, filters.filterGroupOptions)
      );
    }

    // Filter by dropdown options
    if (filters.dropDownOptions) {
      combinedFilters.push(
        filterRecipeByOptions(recipe, filters.dropDownOptions, 'and')
      );
    }

    return combinedFilters.length === 0
      ? true
      : combinedFilters.every((filter) => filter);
  });
};

export const getGraphqlFilterFromRecipeSearchScreenFilters = (
  filters: SearchScreenFilterValues
): ListRecipesQueryVariables | undefined => {
  const { name, dropDownOptions, filterGroupOptions } = filters;
  let filter: ListRecipesQueryVariables | undefined = undefined;

  const combinedFilters = [];

  // Add name filter to search params
  if (name && name !== '') {
    combinedFilters.push({
      searchField: { contains: name.toLowerCase() },
    });
  }

  // Add filter group options to search params
  if (filterGroupOptions && filterGroupOptions.length > 0) {
    const optFilters = filterGroupOptions
      .map(getRecipeGraphqlFilterForOption)
      .filter(notNullOrUndefined);

    optFilters.length &&
      combinedFilters.push({
        or: optFilters,
      });
  }

  // Add dropdown filter options to search params
  if (dropDownOptions && dropDownOptions.length > 0) {
    const optFilters = dropDownOptions
      .map(getRecipeGraphqlFilterForOption)
      .filter(notNullOrUndefined);

    const categories: string[] = Array.from(
      new Set(Object.values(optFilters).map((opt) => Object.keys(opt)[0]))
    );

    categories.forEach((cat) => {
      const optsOfSameCat = {
        or: optFilters.filter((opt) => Object.keys(opt)[0] === cat),
      };
      combinedFilters.push({
        and: optsOfSameCat,
      });
    });
  }

  const allFiltersMustMatch = combinedFilters.length > 1;
  const onlyOneFilterIsSet = combinedFilters.length === 1;

  if (allFiltersMustMatch) {
    filter = { filter: { and: combinedFilters } };
  } else if (onlyOneFilterIsSet) {
    filter = { filter: combinedFilters[0] };
  }

  return filter;
};

/**
 * Generates graphql recipe filter from given option
 */
export const getRecipeGraphqlFilterForOption = (
  option: OptionModel
): ModelRecipeFilterInput | undefined => {
  const optionID = option.id;
  const eqOrNe = option.excl ? 'ne' : 'eq';
  const containsOrNotContains = option.excl ? 'notContains' : 'contains';

  switch (option.type) {
    case 'skill_level':
      return { skillLevelID: { [eqOrNe]: optionID } };

    case 'budget':
      const budgetVal = Number(option.label.charAt(0));
      return { budget: { [eqOrNe]: budgetVal } };

    case 'preparation_time':
      return { prepTimeID: { [eqOrNe]: optionID } };

    case 'main_ingredient':
      return { mainIngredientID: { [eqOrNe]: optionID } };

    case 'dish_type':
      return { dishTypeID: { [eqOrNe]: optionID } };

    case 'serving_size':
      return { servingSizeID: { [eqOrNe]: optionID } };

    case 'special_diet':
      return { specialDietIDs: { [containsOrNotContains]: optionID } };

    case 'cuisine':
      return { cuisineTypeID: { [containsOrNotContains]: optionID } };

    case 'allergy':
      return { allergyIDs: { [containsOrNotContains]: optionID } };

    case 'meal_type':
      return { mealTypes: { [containsOrNotContains]: optionID as MealType } };

    default:
      return undefined;
  }
};
